/**
 * Overlays — modal dialogs and non-modal popovers share ONE stack.
 *
 *  - Modal overlays (drawer, confirm) are a native <dialog> opened with showModal(): the browser gives
 *    focus containment, the top layer and an inert background. No homegrown focus trap.
 *    A controller (a BaseController) owns the <dialog> and drives it with `ModalOverlay`.
 *  - Non-modal panels (menu, combobox list, date picker, dropdown-more) extend `PopoverController`:
 *    no focus trap, one open at a time (`ls:popover-open`), closes on outside press and Esc,
 *    position = `data-placement` (CSS does the rest).
 *  - The stack: only the TOP overlay handles Esc; focus returns to the invoker (or a declared fallback);
 *    scroll lock (`data-scroll-lock` on <html>) is reference-counted. A popover inside a dialog lives
 *    in that dialog's DOM.
 *
 *   <div data-controller="menu">
 *     <button data-menu-target="trigger" data-action="menu#toggle" aria-expanded="false">…</button>
 *     <div data-menu-target="panel" data-placement="bottom-start" hidden>…</div>
 *   </div>
 */
import { BaseController } from './base_controller.js'
import { emit } from './events.js'
import { Lifetime } from './lifetime.js'

/** @typedef {{ modal: boolean, panel: HTMLElement, owns: (node: Node) => boolean, requestClose: (reason: string) => boolean, close: (options?: { restoreFocus?: boolean, reason?: string }) => void }} StackEntry */

/** @type {StackEntry[]} open overlays, topmost last */
const stack = []
/** @type {Lifetime | null} document-level listeners, alive only while something is open */
let listeners = null
let scrollLocks = 0

export const PLACEMENTS = Object.freeze(['bottom-start', 'bottom-end', 'top-start', 'top-end', 'viewport'])

/** @param {StackEntry} entry */
function push(entry) {
  stack.push(entry)
  if (listeners) return
  listeners = new Lifetime()
  listeners.listen(document, 'keydown', onKeydown, { capture: true })
  listeners.listen(document, 'pointerdown', onPointerDown, { capture: true })
}

/** @param {StackEntry} entry */
function pop(entry) {
  const index = stack.indexOf(entry)
  if (index >= 0) stack.splice(index, 1)
  if (stack.length === 0) {
    listeners?.dispose()
    listeners = null
  }
}

/** @param {KeyboardEvent} event */
function onKeydown(event) {
  const top = stack.at(-1)
  if (!top || event.key !== 'Escape' || event.isComposing) return
  event.preventDefault()
  event.stopPropagation() // Escape belongs to the top overlay and to nobody else
  top.requestClose('escape')
}

/** @param {Event} event */
function onPointerDown(event) {
  const target = /** @type {Node} */ (event.target)
  for (let top = stack.at(-1); top && !top.modal && !top.owns(target); top = stack.at(-1)) {
    if (!top.requestClose('outside')) break
  }
}

/** Reference-counted; CSS does the locking: `[data-scroll-lock] { overflow: hidden }`. Returns the release. */
function lockScroll() {
  if (scrollLocks++ === 0) document.documentElement.setAttribute('data-scroll-lock', '')
  return () => {
    if (--scrollLocks === 0) document.documentElement.removeAttribute('data-scroll-lock')
  }
}

/** Can focus go to this node now (and, with a modal on top, stay inside it)? */
function usable(node) {
  if (!(node instanceof HTMLElement) || !node.isConnected || node.closest('[hidden], [inert]') || node.disabled) return false
  const modal = [...stack].reverse().find((entry) => entry.modal)
  return !modal || modal.panel.contains(node)
}

/** @param {(HTMLElement | null | undefined)[]} candidates */
function restoreFocus(candidates) {
  const target = candidates.find(usable)
  target?.focus({ preventScroll: true })
}

/**
 * Drives one native <dialog> as a modal overlay on the shared stack. Used by controllers that own a
 * dialog (drawer, confirm). Everything it holds is released with `lifetime`.
 *
 *   this.overlay = new ModalOverlay(this.lifetime, this.dialogTarget, {
 *     beforeClose: (reason) => (this.dirty ? (this.askConfirm(), false) : true),
 *   })
 *   this.overlay.open({ returnFocus: invoker })
 */
export class ModalOverlay {
  /** @type {Lifetime | null} everything that exists only while open */
  #session = null
  /** @type {Element | null} */
  #returnFocus = null

  /**
   * @param {Lifetime} lifetime
   * @param {HTMLDialogElement} dialog
   * @param {{
   *   fallbackFocus?: HTMLElement | null,
   *   beforeClose?: ((reason: string) => boolean | void) | null,
   *   onClose?: ((reason: string) => void) | null,
   * }} [options]
   *   beforeClose: return `false` to veto a close request (unsaved changes); close() is unconditional.
   *   onClose: called after every close except disposal (the owner emits its own `closed` event there).
   */
  constructor(lifetime, dialog, { fallbackFocus = null, beforeClose = null, onClose = null } = {}) {
    if (!(dialog instanceof HTMLDialogElement) || typeof dialog.showModal !== 'function') {
      throw new TypeError('overlay: a native <dialog> with showModal() is required for a modal overlay')
    }
    this.lifetime = lifetime
    this.dialog = dialog
    this.fallbackFocus = fallbackFocus
    this.beforeClose = beforeClose
    this.onClose = onClose
    lifetime.add(() => this.close({ restoreFocus: false, reason: 'dispose' }))
    // Esc normally never reaches here (the stack consumes it first); this is the native safety net.
    lifetime.listen(dialog, 'cancel', (event) => {
      event.preventDefault()
      if (stack.at(-1) === this) this.requestClose('escape')
    })
    // The dialog was closed natively (<form method="dialog">): bring our state in line.
    lifetime.listen(dialog, 'close', () => {
      if (this.isOpen && !dialog.open) this.close({ reason: 'native' })
    })
  }

  get modal() {
    return true
  }

  get panel() {
    return this.dialog
  }

  get isOpen() {
    return Boolean(this.#session?.active)
  }

  /** @param {Node} node */
  owns(node) {
    return this.dialog.contains(node)
  }

  /**
   * @param {{ returnFocus?: Element | null }} [options] the invoker; defaults to the focused element
   * @returns {boolean} whether it opened
   */
  open({ returnFocus = null } = {}) {
    if (this.isOpen || !this.lifetime.active) return false
    // popovers are non-modal and would sit behind the dialog's backdrop: close them first
    for (const entry of [...stack]) if (!entry.modal) entry.close({ restoreFocus: false, reason: 'replaced' })
    this.#returnFocus = returnFocus ?? document.activeElement
    const session = (this.#session = this.lifetime.child())
    push(this)
    session.add(() => pop(this))
    try {
      this.dialog.showModal()
    } catch (error) {
      this.#session = null
      session.dispose()
      throw error
    }
    session.add(() => {
      if (this.dialog.open) this.dialog.close()
    })
    session.add(lockScroll())
    this.dialog.querySelector('[data-initial-focus], [autofocus]')?.focus({ preventScroll: true }) // else the browser chooses
    return true
  }

  /**
   * Ask to close; `beforeClose` may veto. Returns whether it closed.
   * @param {string} reason 'escape' | 'dismiss' | 'toggle' | your own
   */
  requestClose(reason) {
    if (!this.isOpen) return false
    if (this.beforeClose?.(reason) === false) return false
    this.close({ reason })
    return true
  }

  /** Close unconditionally. Overlays stacked above this one close first. */
  close({ restoreFocus: restore = true, reason = 'api' } = {}) {
    if (!this.isOpen) return
    while (stack.includes(this) && stack.at(-1) !== this) stack.at(-1).close({ restoreFocus: false, reason: 'parent' })
    const session = this.#session
    this.#session = null
    session.dispose()
    const opener = this.#returnFocus
    this.#returnFocus = null
    if (restore) restoreFocus([/** @type {any} */ (opener), this.fallbackFocus])
    if (reason !== 'dispose') this.onClose?.(reason)
  }
}

/**
 * Pick the side and alignment that fit, flipping at viewport edges; 'viewport' when nothing fits.
 * @param {{ top: number, bottom: number, left: number, right: number }} anchor
 * @param {{ width: number, height: number }} panel
 * @param {{ width: number, height: number }} viewport
 * @param {string} [preferred] '<top|bottom>-<start|end>'
 * @returns {string} one of PLACEMENTS
 */
export function choosePlacement(anchor, panel, viewport, preferred = 'bottom-start') {
  let [side, align] = preferred.split('-')
  const room = { top: anchor.top, bottom: viewport.height - anchor.bottom }
  const opposite = side === 'top' ? 'bottom' : 'top'
  if (room[side] < panel.height) {
    if (room[opposite] < panel.height) return 'viewport'
    side = opposite
  }
  if (panel.width > viewport.width) return 'viewport'
  const fitsStart = anchor.left + panel.width <= viewport.width
  const fitsEnd = anchor.right - panel.width >= 0
  if (align === 'end') {
    if (!fitsEnd) {
      if (!fitsStart) return 'viewport'
      align = 'start'
    }
  } else if (!fitsStart) {
    if (!fitsEnd) return 'viewport'
    align = 'end'
  }
  return `${side}-${align === 'end' ? 'end' : 'start'}`
}

/**
 * Non-modal overlay anchored to its trigger: menu, combobox list, date picker, dropdown-more.
 * Targets: `panel` (defaults to the controller element), `trigger` (optional). Actions: `toggle`, `open`, `close`.
 * Writes `hidden` on the panel, `aria-expanded` on the trigger and `data-placement` on the panel (the placement
 * in the markup is the preferred one); CSS positions it. It does NOT write `data-state` on the panel: that
 * attribute belongs to render.showState (loading|empty|error|ready) and a combobox passes its panel to it.
 * A modal <dialog> is styled by `[open]`.
 * Events: `<identifier>:opened`, `<identifier>:closed` ({ reason }); window `ls:popover-open` (bubbling from the
 * element) makes every other popover close, except the chain this one is nested in.
 */
export class PopoverController extends BaseController {
  static targets = ['panel', 'trigger']

  /** @type {Lifetime | null} everything that exists only while open */
  #session = null
  /** @type {Element | null} */
  #returnFocus = null
  #preferred = 'bottom-start'

  get modal() {
    return false
  }

  /** @returns {HTMLElement} */
  get panel() {
    return this.hasPanelTarget ? this.panelTarget : this.element
  }

  get isOpen() {
    return Boolean(this.#session?.active)
  }

  /** @param {Node} node */
  owns(node) {
    return this.panel.contains(node) || (this.hasTriggerTarget && this.triggerTarget.contains(node))
  }

  prepare() {
    const panel = this.panel
    const preferred = panel.getAttribute('data-placement')
    this.#preferred = PLACEMENTS.includes(preferred) && preferred !== 'viewport' ? preferred : 'bottom-start'
    panel.hidden = true
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', 'false')
    this.onDispose(() => this.close({ restoreFocus: false, reason: 'dispose' }))
    // another popover opened somewhere: close, unless it is nested inside this panel
    this.listen(window, 'ls:popover-open', (event) => {
      const source = /** @type {Node} */ (event.target)
      if (!this.isOpen || source === this.element || this.panel.contains(source)) return
      this.close({ restoreFocus: false, reason: 'replaced' })
    })
  }

  /** @returns {boolean} whether it opened */
  open() {
    if (this.isOpen || !this.lifetime) return false
    emit(this.element, 'ls', 'popover-open') // the others close before this one appears
    const panel = this.panel
    const anchor = this.hasTriggerTarget ? this.triggerTarget : this.element
    this.#returnFocus = this.hasTriggerTarget ? this.triggerTarget : document.activeElement
    const session = (this.#session = this.lifetime.child())
    push(this)
    session.add(() => pop(this))
    panel.hidden = false
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', 'true')
    session.add(() => {
      panel.hidden = true
      if (this.hasTriggerTarget) this.triggerTarget.setAttribute('aria-expanded', 'false')
    })
    const place = () => {
      const size = panel.getBoundingClientRect()
      const viewport = { width: document.documentElement.clientWidth, height: document.documentElement.clientHeight }
      panel.setAttribute('data-placement', choosePlacement(anchor.getBoundingClientRect(), size, viewport, this.#preferred))
    }
    place()
    session.listen(window, 'resize', place, { passive: true })
    session.listen(window, 'scroll', place, { passive: true, capture: true })
    panel.querySelector('[data-initial-focus], [autofocus]')?.focus({ preventScroll: true })
    this.emit('opened')
    return true
  }

  toggle() {
    if (this.isOpen) this.requestClose('toggle')
    else this.open()
  }

  /** @param {string} reason 'escape' | 'outside' | 'toggle' | your own @returns {boolean} whether it closed */
  requestClose(reason) {
    if (!this.isOpen) return false
    this.close({ reason, restoreFocus: reason !== 'outside' })
    return true
  }

  /** Close unconditionally (also usable as an action). Popovers stacked above this one close first. */
  close({ restoreFocus: restore = true, reason = 'api' } = {}) {
    if (!this.isOpen) return
    while (stack.includes(this) && stack.at(-1) !== this) stack.at(-1).close({ restoreFocus: false, reason: 'parent' })
    const session = this.#session
    this.#session = null
    session.dispose()
    const opener = this.#returnFocus
    this.#returnFocus = null
    if (restore) restoreFocus([/** @type {any} */ (opener)])
    if (this.lifetime?.active) this.emit('closed', { reason })
  }
}
